// src/utils/routePermissions.ts

import axios from '@/config/axios';
import { isRestrictedRoute } from './restrictedRoutes';

interface RoutePermission {
    path: string;
    allowedGroups: string[];
    requiresAdminMaster: boolean;
    isActive?: boolean;
}

// Cache de permissões de rotas
export let routePermissionsCache: Map<string, RoutePermission> = new Map();
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Busca as permissões de rotas do backend
 */
export async function fetchRoutePermissions(): Promise<Map<string, RoutePermission>> {
    const now = Date.now();

    // Se o cache ainda é válido, retornar cache
    if (now - cacheTimestamp < CACHE_DURATION && routePermissionsCache.size > 0) {
        return routePermissionsCache;
    }

    try {
        const response = await axios.get('/routes');
        const routes = response.data.data || [];

        const permissionsMap = new Map<string, RoutePermission>();

        routes.forEach((route: any) => {
            // Processar TODAS as rotas (ativas ou não) para verificação
            // A verificação de isActive será feita durante o acesso
            if (route.path) {
                const allowedGroups = (route.permissions || []).map((p: any) => p.allowedGroup);

                permissionsMap.set(route.path, {
                    path: route.path,
                    allowedGroups,
                    requiresAdminMaster: route.requiresAdminMaster || false,
                    isActive: route.isActive !== false // Default true se não especificado
                });
            }
        });

        routePermissionsCache = permissionsMap;
        cacheTimestamp = now;

        return permissionsMap;
    } catch (error) {
        console.error('❌ Erro ao buscar permissões de rotas:', error);
        return routePermissionsCache; // Retornar cache em caso de erro
    }
}

/**
 * Verifica se o usuário tem acesso a uma rota específica
 * ✅ ATUALIZADO: Agora também verifica permissões de user_route_level
 * @param routePath - Path da rota
 * @param userGroups - Grupos do usuário (LDAP)
 * @param isAdminMaster - Se é admin master
 * @param userPermissions - Permissões do usuário vindas do backend (user_route_level)
 * @returns objeto com { hasAccess: boolean, shouldLogout: boolean }
 */
export function hasRouteAccess(
    routePath: string,
    userGroups: string[],
    isAdminMaster: boolean,
    userPermissions?: {
        hasAdminAccess: boolean;
        hasEditorAccess: boolean;
        isAdmin: boolean;
        isEditor: boolean;
    }
): { hasAccess: boolean; shouldLogout: boolean } {
    // Admin master tem acesso a tudo — EXCETO rotas de dados sensíveis, que
    // exigem concessão explícita (ver utils/restrictedRoutes.ts).
    if (isAdminMaster && !isRestrictedRoute(routePath)) {
        return { hasAccess: true, shouldLogout: false };
    }

    // ⚠️ REMOVIDO: userPermissions.isAdmin é por rota específica, não global
    // A verificação por rota específica é feita no router guard via routeAccessService
    // Não usar verificação global aqui para não dar acesso indevido

    // Verificar se o path pai está bloqueado (APENAS se o pai estiver configurado no banco)
    const parentPath = getParentPath(routePath);
    if (parentPath && parentPath !== routePath) {
        const parentPermission = routePermissionsCache.get(parentPath);

        // Se o pai ESTÁ configurado no banco, verificar permissões
        if (parentPermission) {
            // Se o pai está inativo, bloquear
            if (parentPermission.isActive === false) {
                return { hasAccess: false, shouldLogout: false };
            }

            // Se o pai requer admin master e usuário não é, bloquear
            if (parentPermission.requiresAdminMaster && !isAdminMaster) {
                return { hasAccess: false, shouldLogout: false };
            }

            // Se o pai tem grupos específicos e usuário não pertence, bloquear
            if (parentPermission.allowedGroups && parentPermission.allowedGroups.length > 0) {
                const hasGroupAccess = parentPermission.allowedGroups.some((allowedGroup: string) => userGroups.includes(allowedGroup));

                if (!hasGroupAccess) {
                    return { hasAccess: false, shouldLogout: false };
                }
            }
        }
        // Se o pai NÃO está configurado no banco, IGNORAR verificação do pai (permitir children)
    }

    // Verificar o path atual
    let routePermission = routePermissionsCache.get(routePath);

    // Se não encontrou a rota exata, tentar encontrar rota dinâmica correspondente
    // Ex: /users/adolfo.castro -> /users/:samaccountname
    if (!routePermission) {
        // Procurar por rotas dinâmicas que correspondem ao padrão
        for (const [cachedPath, cachedPermission] of routePermissionsCache.entries()) {
            // Verificar se a rota cached é um padrão dinâmico (contém :)
            if (cachedPath.includes(':')) {
                // Converter padrão dinâmico para regex
                const pattern = cachedPath.replace(/:[^/]+/g, '[^/]+');
                const regex = new RegExp(`^${pattern}$`);

                // Verificar se o routePath corresponde ao padrão
                if (regex.test(routePath)) {
                    routePermission = cachedPermission;
                    break;
                }
            }
        }
    }

    // Se não há configuração de permissão no banco
    if (!routePermission) {
        // ⚠️ IMPORTANTE: Sem configuração no banco, usar comportamento fail-secure
        // TODAS as rotas devem ser configuradas no banco via painel "Permissões > Rotas"
        // Sem configuração = SEM ACESSO (exceto para Admin Master)
        console.warn(`⚠️ Rota ${routePath} não configurada no banco - bloqueando acesso (fail-secure)`);
        return { hasAccess: false, shouldLogout: false };
    }

    // Se a rota está inativa, bloquear acesso
    if (routePermission.isActive === false) {
        return { hasAccess: false, shouldLogout: false };
    }

    // Se requer admin master e usuário não é admin master, negar
    if (routePermission.requiresAdminMaster && !isAdminMaster) {
        return { hasAccess: false, shouldLogout: false };
    }

    // Se não há grupos permitidos específicos, PERMITIR acesso (rota liberada para todos autenticados)
    if (!routePermission.allowedGroups || routePermission.allowedGroups.length === 0) {
        return { hasAccess: true, shouldLogout: false };
    }

    // Verificar se o usuário pertence a algum dos grupos permitidos
    const hasGroupAccess = routePermission.allowedGroups.some((allowedGroup: string) => userGroups.includes(allowedGroup));

    // Se não tem acesso e há grupos permitidos definidos, apenas bloquear (não deslogar)
    if (!hasGroupAccess) {
        return { hasAccess: false, shouldLogout: false };
    }

    return { hasAccess: true, shouldLogout: false };
}

/**
 * Obtém o path pai de uma rota
 * Ex: /bookings/dashboard -> /bookings
 * Ex: /bookings -> null (já é o pai)
 */
function getParentPath(path: string): string | null {
    const parts = path.split('/').filter((p) => p.length > 0);
    if (parts.length <= 1) {
        return null;
    }
    return '/' + parts[0];
}

/**
 * Verifica se o usuário deve ver um item de menu
 * Versão permissiva para menu (fail-open quando não há configuração)
 * ✅ ATUALIZADO: Agora também verifica permissões de user_route_level
 * @param routePath - Path da rota
 * @param userGroups - Grupos do usuário (LDAP)
 * @param isAdminMaster - Se é admin master
 * @param userPermissions - Permissões do usuário vindas do backend (user_route_level)
 * @param explicitAccessPaths - Paths onde o usuário tem acesso via user_route_level (member/editor/admin). Precedência sobre grupos.
 * @returns boolean
 */
export function hasMenuAccess(
    routePath: string,
    userGroups: string[],
    isAdminMaster: boolean,
    userPermissions?: {
        hasAdminAccess: boolean;
        hasEditorAccess: boolean;
        isAdmin: boolean;
        isEditor: boolean;
    },
    explicitAccessPaths?: string[]
): boolean {
    // Admin master tem acesso a tudo — EXCETO rotas de dados sensíveis, que
    // exigem concessão explícita (ver utils/restrictedRoutes.ts).
    if (isAdminMaster && !isRestrictedRoute(routePath)) {
        return true;
    }

    // ✅ PRECEDÊNCIA: Se o usuário tem acesso explícito via user_route_level para esta rota, mostrar no menu
    if (explicitAccessPaths && explicitAccessPaths.length > 0) {
        if (explicitAccessPaths.includes(routePath)) return true;
        // Match rotas dinâmicas (ex: /users/:samaccountname vs /users/john.doe)
        for (const pattern of explicitAccessPaths) {
            if (pattern.includes(':')) {
                const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
                if (regex.test(routePath)) return true;
            }
        }
    }

    // Verificar se o path pai está bloqueado
    const parentPath = getParentPath(routePath);
    if (parentPath && parentPath !== routePath) {
        const parentPermission = routePermissionsCache.get(parentPath);

        if (parentPermission) {
            // Se o pai está inativo, bloquear
            if (parentPermission.isActive === false) {
                return false;
            }

            // Se o pai requer admin master e usuário não é, bloquear
            if (parentPermission.requiresAdminMaster && !isAdminMaster) {
                return false;
            }

            // Se o pai tem grupos específicos e usuário não pertence, bloquear
            if (parentPermission.allowedGroups && parentPermission.allowedGroups.length > 0) {
                const hasGroupAccess = parentPermission.allowedGroups.some((allowedGroup: string) => userGroups.includes(allowedGroup));

                if (!hasGroupAccess) {
                    return false;
                }
            }
        }
    }

    // Verificar o path atual
    const routePermission = routePermissionsCache.get(routePath);

    // Se não há configuração no banco
    if (!routePermission) {
        // ⚠️ IMPORTANTE: Sem configuração no banco, não mostrar no menu (fail-secure)
        // TODAS as rotas devem ser configuradas no banco via painel "Permissões > Rotas"
        // Sem configuração = NÃO MOSTRAR (exceto para Admin Master que vê tudo)
        console.warn(`⚠️ Rota ${routePath} não configurada no banco - ocultando do menu (fail-secure)`);
        return false;
    }

    // Se a rota está inativa, bloquear acesso
    if (routePermission.isActive === false) {
        return false;
    }

    // Se requer admin master e usuário não é admin master, negar
    if (routePermission.requiresAdminMaster && !isAdminMaster) {
        return false;
    }

    // Se não há grupos permitidos específicos, PERMITIR acesso (rota liberada para todos autenticados)
    if (!routePermission.allowedGroups || routePermission.allowedGroups.length === 0) {
        return true;
    }

    // Verificar se o usuário pertence a algum dos grupos permitidos
    const hasGroupAccess = routePermission.allowedGroups.some((allowedGroup: string) => userGroups.includes(allowedGroup));

    // Se não tem acesso e há grupos permitidos definidos, bloquear do menu
    if (!hasGroupAccess) {
        return false;
    }

    return true;
}

/**
 * Verifica se há configuração de permissão no banco para uma rota
 */
export function hasRouteConfiguration(routePath: string): boolean {
    return routePermissionsCache.has(routePath);
}

/**
 * Limpa o cache de permissões (útil após atualizar permissões)
 */
export function clearRoutePermissionsCache() {
    routePermissionsCache.clear();
    cacheTimestamp = 0;
}
